'use client';

import React from 'react';
import { LocalizedLink } from './LocalizedLink';
import { getApiBaseUrl } from '@/lib/api';
import { useTranslations } from '@/hooks/useTranslations';
import { getAutoExternalRel } from '@/lib/link-rel';
import type { GeoCountry } from '@/lib/geolocation';

type FooterProps = {
  geoCountry?: GeoCountry | null;
};

export function Footer({ geoCountry = null }: FooterProps) {
  const { t } = useTranslations();
  const year = new Date().getFullYear();
  const [newsletterEmail, setNewsletterEmail] = React.useState('');
  const [newsletterStatus, setNewsletterStatus] = React.useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const newsletterMessageId = newsletterStatus === 'success' || newsletterStatus === 'error' ? 'footer-newsletter-status' : undefined;

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newsletterEmail.trim();
    if (!email) return;
    setNewsletterStatus('loading');
    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/auth/newsletter-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || `Request failed: ${res.status}`);
      }
      setNewsletterEmail('');
      setNewsletterStatus('success');
    } catch {
      setNewsletterStatus('error');
    }
  };

  return (
    <footer className="w-full border-t border-white/5 bg-[#0B0D19]">
      <div className="mx-auto w-full max-w-6xl px-6 py-12">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="mb-4 font-bold text-white">HotlistCasino</p>
            <p className="text-xs leading-relaxed text-slate-400">
              {t('footer.tagline')}
            </p>
          </div>

          <div>
            <p className="mb-4 text-sm font-semibold text-white">{t('footer.quickLinks')}</p>
            <ul className="space-y-2 text-xs text-slate-400">
              <li>
                <LocalizedLink className="transition-colors hover:text-white" href="/casinos">
                  {t('nav.casinos')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink className="transition-colors hover:text-white" href="/games">
                  {t('nav.games')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink className="transition-colors hover:text-white" href="/game-providers">
                  {t('nav.gameProviders')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink className="transition-colors hover:text-white" href="/blog">
                  {t('nav.blog')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink className="transition-colors hover:text-white" href="/about">
                  {t('nav.about')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink className="transition-colors hover:text-white" href="/faq">
                  {t('nav.faq')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink className="transition-colors hover:text-white" href="/contact">
                  {t('nav.contact')}
                </LocalizedLink>
              </li>
            </ul>
          </div>

          <div>
            <p className="mb-4 text-sm font-semibold text-white">{t('footer.legal')}</p>
            <ul className="space-y-2 text-xs text-slate-400">
              <li>
                <LocalizedLink className="transition-colors hover:text-white" href="/terms">
                  {t('footer.termsOfService')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink className="transition-colors hover:text-white" href="/privacy-policy">
                  {t('footer.privacyPolicy')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink className="transition-colors hover:text-white" href="/cookies-policy">
                  {t('footer.cookiePolicy')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink className="transition-colors hover:text-white" href="/responsible-gaming">
                  {t('footer.responsibleGaming')}
                </LocalizedLink>
              </li>
            </ul>
          </div>

          <div>
            <p className="mb-4 text-sm font-semibold text-white">{t('footer.followUs')}</p>
            <a
              className="inline-flex h-10 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
              href="https://t.me/hotlistcasino"
              target="_blank"
              rel={getAutoExternalRel('https://t.me/hotlistcasino')}
              aria-label="Telegram channel hotlistcasino"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
              </svg>
              Telegram
            </a>
            <form onSubmit={handleNewsletterSubmit} className="mt-4 max-w-xs">
              <label htmlFor="footer-newsletter-email" className="block text-sm font-semibold text-white">
                {t('footer.subscribeNewsletter')}
              </label>
              <p id="footer-newsletter-hint" className="mt-1 text-xs leading-5 text-slate-400">
                {t('footer.newsletterDescription')}
              </p>
              <div className="mt-2 flex items-center rounded-full border border-white/10 bg-white/5 p-1 transition focus-within:border-[#ffb74d]/60">
                <input
                  id="footer-newsletter-email"
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => {
                    setNewsletterEmail(e.target.value);
                    if (newsletterStatus === 'error') setNewsletterStatus('idle');
                  }}
                  placeholder={t('footer.yourEmail')}
                  required
                  disabled={newsletterStatus === 'loading'}
                  autoComplete="email"
                  aria-describedby={['footer-newsletter-hint', newsletterMessageId].filter(Boolean).join(' ')}
                  className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={newsletterStatus === 'loading'}
                  aria-label={t('footer.subscribeButton')}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#ffb74d] text-[#3a2100] transition hover:bg-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d] focus:ring-offset-2 focus:ring-offset-[#0B0D19] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {newsletterStatus === 'loading' ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#3a2100] border-t-transparent" aria-hidden />
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M22 2L11 13" />
                      <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                    </svg>
                  )}
                </button>
              </div>
              {newsletterStatus === 'success' && (
                <p id="footer-newsletter-status" className="mt-3 rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-xs leading-5 text-emerald-100" role="status" aria-live="polite">
                  {t('footer.thanksSubscribed')}
                </p>
              )}
              {newsletterStatus === 'error' && (
                <p id="footer-newsletter-status" className="mt-3 rounded-xl border border-rose-400/20 bg-rose-400/10 px-3 py-2 text-xs leading-5 text-rose-100" role="alert">
                  {t('footer.subscribeError')}
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="mt-8 border-t border-white/5 pt-6 text-center text-xs text-slate-400">
          © {year} HotlistCasino. {t('footer.copyright')} | 19+ | {t('footer.playResponsibly')}
          {geoCountry ? (
            <p className="mt-2 text-[10px] text-slate-400">
              {geoCountry.flag ? `${geoCountry.flag} ` : ''}
              {geoCountry.name ?? geoCountry.code} ({geoCountry.code})
            </p>
          ) : (
            <p className="mt-2 text-[10px] text-slate-400">—</p>
          )}
        </div>
      </div>
    </footer>
  );
}
